import { zodResolver } from '@hookform/resolvers/zod'
import { isFuture } from 'date-fns'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import DatePicker from '@/components/DatePicker/DatePicker'
import FormField from '@/components/FormField'
import ModalForm from '@/components/Modal/ModalForm'
import RadioGroup from '@/components/Radio/RadioGroup'
import UnableToPerfomAction from '@/components/common/UnableToPerfomAction'
import { formSchema, type FormSchema } from '@/constants/archive-payload'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { toast } from '@/hooks/useToast'
import { formatApiDate } from '@/utils/format-date'

import styles from './ArchiveUserModal.module.scss'
import useScheduleArchiveUsers from '../hooks/useScheduleArchiveUsers'
import useBulkArchiveUsers from '../mutations/useBulkArchiveUsers'
import useBulkUnableArchiveUsers from '../queries/useBulkUnableArchiveUsers'

type ArchiveUserModalProps = {
  userName?: string
  ids: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onArchive: () => void
  children?: React.ReactNode
}

const ArchiveUserModal = (props: ArchiveUserModalProps) => {
  const { t } = useTranslation('users')

  const count = props.ids.length

  const handleOpenChange = (open: boolean) => {
    props.onOpenChange(open)
    if (!open) {
      setPage(1)
      form.reset()
    }
  }

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    mode: 'all',
    defaultValues: {
      archiveMode: 'now',
      archiveDate: undefined
    }
  })

  const isDateValid = (date: Date) => isFuture(date)

  const archiveMode = form.watch('archiveMode')
  const isArchiveScheduled = archiveMode === 'scheduled'
  const archiveDate = isArchiveScheduled ? form.watch('archiveDate') : null

  const shouldDateBeDisabled = (date: Date) => !isDateValid(date)

  const [page, setPage] = useState(1)

  const { data, isLoading: isUsersLoading } = useBulkUnableArchiveUsers({
    page,
    ids: props.ids,
    archiveDate: archiveDate ? formatApiDate(archiveDate) : null,
    enabled: !!props.ids.length
  })

  const unableToPerformActionDataCount = data ? data.length : 0

  const radioOptions = [
    {
      label: t('text.archive-now'),
      value: 'now'
    },
    { label: t('text.schedule-archiving'), value: 'scheduled' }
  ]

  const handleClose = () => {
    props.onOpenChange(false)
    setPage(1)
    form.reset()
  }

  const handleSuccesArchive = () => {
    props.onArchive()
    handleClose()
  }

  const { mutate: bulkArchiveUsers, isPending: isBulkArchiveUsersLoading } =
    useBulkArchiveUsers({
      onSuccess: () => {
        handleSuccesArchive()
      },
      onError: () => {
        toast({
          variant: 'error',
          title: t('toast.users-archived-failed', {
            count: props.ids.length
          })
        })
      },
      userName: props.userName
    })

  const {
    mutate: bulkScheduledArchiveUsers,
    isPending: isBulkScheduledArchiveUsersLoading
  } = useScheduleArchiveUsers({
    userName: props.userName,
    onSuccess: () => {
      handleSuccesArchive()
    }
  })

  const handleSubmit = () => {
    if (archiveDate) {
      bulkScheduledArchiveUsers({
        ids: props.ids,
        archiveDate
      })
    } else {
      bulkArchiveUsers({
        ids: props.ids
      })
    }
  }

  return (
    <ModalForm
      title={t('header.archive-users', {
        count
      })}
      open={props.open}
      onOpenChange={handleOpenChange}
      loading={isBulkArchiveUsersLoading || isBulkScheduledArchiveUsersLoading}
      form={form}
      onSubmit={handleSubmit}
      submitButtonVariant="danger"
      boxClassName={styles.modal}
      submitText={t('button.archive')}
      onCancel={handleClose}
      onClose={handleClose}
    >
      <p className={styles.description}>
        {props.userName
          ? t('help.want-to-archive-user', {
              NAME: props.userName
            })
          : t('help.want-to-archive-users', {
              count
            })}
      </p>
      <div className={styles.wrapper}>
        <FormField
          control={form.control}
          id="archive-mode"
          label={t('label.archive-mode')}
          hideLabel
          required
          name="archiveMode"
          render={({ inputProps }) => (
            <RadioGroup
              {...inputProps}
              options={radioOptions}
              onChange={value => {
                inputProps.onChange?.(value)
                setPage(1)
              }}
            />
          )}
        />

        {isArchiveScheduled ? (
          <FormField
            control={form.control}
            id="archive-date"
            label={t('label.scheduled-date')}
            hideLabel
            className={styles.datePickerWrapper}
            required={isArchiveScheduled}
            name="archiveDate"
            render={({ inputProps }) => (
              <DatePicker
                disabledDate={shouldDateBeDisabled}
                placeholder={t('placeholder.select-date')}
                {...inputProps}
              />
            )}
          />
        ) : null}
      </div>
      <UnableToPerfomAction
        alertTitle={t('help.unable-to-archive-users', {
          count: unableToPerformActionDataCount
        })}
        alertDescription={t('help.following-users-cannot-be-archived')}
        unableToPerformActionData={data || []}
        count={unableToPerformActionDataCount}
        page={page}
        pageSize={DEFAULT_PAGE_SIZE}
        isTableLoading={isUsersLoading}
        onPageChange={setPage}
      />
    </ModalForm>
  )
}

export default ArchiveUserModal
